'use client';

import { useEffect, useRef } from 'react';

export type DatadogRumConfig = {
  isEnabled?: string;
  applicationId?: string;
  clientToken?: string;
  site?: string;
  env?: string;
  serviceName?: string;
  version?: string;
  sessionSampleRate?: string;
  sessionReplaySampleRate?: string;
};

export type DatadogRumProps = {
  config?: DatadogRumConfig;
  defaultServiceName: string;
};

const normalizeSampleRate = (value: string | undefined, defaultRate: number): number => {
  if (value === undefined || value === '') return defaultRate;

  const sampleRate = Number(value);

  if (!Number.isFinite(sampleRate)) return defaultRate;

  return Math.min(100, Math.max(0, sampleRate));
};

const DatadogRum = ({ config, defaultServiceName }: DatadogRumProps): null => {
  const isInitialized = useRef(false);

  useEffect(() => {
    if (isInitialized.current) return;
    if (!config || config.isEnabled !== 'true') return;

    const { applicationId, clientToken } = config;
    if (!applicationId || !clientToken) {
      if (process.env.NODE_ENV === 'production') return;
      console.warn('[Datadog RUM] Missing applicationId or clientToken - skipping initialization.');
      return;
    }

    import('@datadog/browser-rum').then(({ datadogRum }) => {
      datadogRum.init({
        applicationId,
        clientToken,
        site: config.site || 'datadoghq.eu',
        service: config.serviceName || defaultServiceName,
        env: config.env || 'dev',
        version: config.version || undefined,
        sessionSampleRate: normalizeSampleRate(config.sessionSampleRate, 10),
        sessionReplaySampleRate: normalizeSampleRate(config.sessionReplaySampleRate, 0),
        trackResources: true,
        trackUserInteractions: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
      });

      isInitialized.current = true;
    });
  }, [config, defaultServiceName]);

  return null;
};

export default DatadogRum;
